/**
 * Public Library browse & search page.
 *
 * The search corpus (index.json) and content blobs are served statically from
 * the Cloudflare R2 CDN — all reads bypass AWS. Rating and favoriting hit the
 * authenticated backend.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Rating,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  libraryApi,
  type LibraryBlob,
  type LibraryBrowseEntry,
  type LibraryItemDetail,
  type LibraryItemType,
  type LibraryPackContent,
  type LibraryPaiqAnalysis,
  type LibraryProjectContent,
  type LibraryPromptContent,
} from '../../services/api';

type SortKey = 'recent' | 'rating' | 'favorites';

const PAIQ_DIMENSION_LABELS: Array<[string, string]> = [
  ['firstPrinciplesDepth', 'First Principles Depth'],
  ['cognitiveConstraints', 'Cognitive Constraints'],
  ['crossDomainSynthesis', 'Cross-Domain Synthesis'],
  ['specificity', 'Specificity'],
  ['signalToNoise', 'Signal-to-Noise'],
  ['actionability', 'Actionability'],
  ['critiqueLoop', 'Critique Loop'],
];

const TYPE_LABEL: Record<LibraryItemType, string> = {
  prompt: 'Prompt',
  pack: 'Pack',
  project: 'Project',
};

const TYPE_COLOR: Record<LibraryItemType, 'primary' | 'secondary' | 'success'> = {
  prompt: 'primary',
  pack: 'secondary',
  project: 'success',
};

function scoreEntry(entry: LibraryBrowseEntry, terms: string[]): number {
  if (terms.length === 0) return 1;
  const haystack = `${entry.title} ${entry.description} ${entry.tags.join(' ')} ${
    entry.parentTitle ?? ''
  } ${entry.ownerName ?? ''}`.toLowerCase();
  let score = 0;
  for (const term of terms) {
    if (!haystack.includes(term)) return 0; // all terms must match
    if (entry.title.toLowerCase().includes(term)) score += 3;
    else if (entry.tags.some((t) => t.toLowerCase().includes(term))) score += 2;
    else score += 1;
  }
  return score;
}

export function LibraryPage() {
  const { code: routeCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LibraryBrowseEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | LibraryItemType>('all');
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [selected, setSelected] = useState<LibraryBrowseEntry | null>(null);
  const [ownedCodes, setOwnedCodes] = useState<Set<string>>(new Set());
  const [pendingRemove, setPendingRemove] = useState<LibraryBrowseEntry | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    libraryApi
      .getIndex()
      .then((data) => {
        if (active) setEntries(data);
      })
      .catch((err) => {
        if (active) setLoadError(err instanceof Error ? err.message : 'Failed to load library');
      });
    return () => {
      active = false;
    };
  }, []);

  // Load the signed-in user's own published items so we can offer a direct
  // remove action on their cards. Silently ignored when not signed in.
  useEffect(() => {
    let active = true;
    libraryApi
      .listMine()
      .then((r) => {
        if (active) setOwnedCodes(new Set(r.data.map((e) => e.code)));
      })
      .catch(() => {
        /* not signed in or no owned items — no owner controls */
      });
    return () => {
      active = false;
    };
  }, []);

  // Deep link: /library/:code opens that item's detail directly.
  useEffect(() => {
    if (!routeCode || !entries) return;
    const match = entries.find((e) => e.code === routeCode);
    if (match) setSelected((prev) => (prev?.code === routeCode ? prev : match));
  }, [routeCode, entries]);

  const openEntry = (entry: LibraryBrowseEntry) => {
    setSelected(entry);
    navigate(`/library/${entry.code}`, { replace: false });
  };

  const closeEntry = () => {
    setSelected(null);
    navigate('/library', { replace: false });
  };

  const handleDeleted = (code: string) => {
    setEntries((prev) => (prev ? prev.filter((e) => e.code !== code) : prev));
    setOwnedCodes((prev) => {
      const next = new Set(prev);
      next.delete(code);
      return next;
    });
    closeEntry();
  };

  const handleRemove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await libraryApi.unpublish(pendingRemove.code);
      const code = pendingRemove.code;
      setEntries((prev) => (prev ? prev.filter((e) => e.code !== code) : prev));
      setOwnedCodes((prev) => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
      setPendingRemove(null);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove item');
    } finally {
      setRemoving(false);
    }
  };

  const results = useMemo(() => {
    if (!entries) return [];
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let filtered = entries
      .filter((e) => typeFilter === 'all' || e.type === typeFilter)
      .map((e) => ({ entry: e, score: scoreEntry(e, terms) }))
      .filter((r) => r.score > 0);

    filtered = filtered.sort((a, b) => {
      if (terms.length > 0 && b.score !== a.score) return b.score - a.score;
      if (sortKey === 'rating') return b.entry.ratingAvg - a.entry.ratingAvg;
      if (sortKey === 'favorites') return b.entry.favoriteCount - a.entry.favoriteCount;
      return b.entry.publishedAt.localeCompare(a.entry.publishedAt);
    });
    return filtered.map((r) => r.entry);
  }, [entries, query, typeFilter, sortKey]);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Public Library
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Discover and reuse prompts, packs, and projects shared by the community.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by title, tag, or description…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as 'all' | LibraryItemType)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All types</MenuItem>
          <MenuItem value="prompt">Prompts</MenuItem>
          <MenuItem value="pack">Packs</MenuItem>
          <MenuItem value="project">Projects</MenuItem>
        </TextField>
        <TextField
          select
          label="Sort"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="recent">Most recent</MenuItem>
          <MenuItem value="rating">Top rated</MenuItem>
          <MenuItem value="favorites">Most favorited</MenuItem>
        </TextField>
      </Stack>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {!entries && !loadError && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {entries && results.length === 0 && !loadError && (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          No library items match your search.
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
        }}
      >
        {results.map((entry) => (
          <Card key={entry.code} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardActionArea sx={{ flexGrow: 1, alignItems: 'stretch' }} onClick={() => openEntry(entry)}>
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                  <Chip size="small" color={TYPE_COLOR[entry.type]} label={TYPE_LABEL[entry.type]} />
                  {typeof entry.paiqScore === 'number' && (
                    <Chip size="small" variant="outlined" label={`PAIQ ${entry.paiqScore}/35`} />
                  )}
                </Stack>
                <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600 }} noWrap>
                  {entry.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 40,
                  }}
                >
                  {entry.description || 'No description provided.'}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {entry.tags.slice(0, 4).map((tag) => (
                    <Chip key={tag} size="small" variant="outlined" label={tag} />
                  ))}
                </Stack>
              </CardContent>
            </CardActionArea>
            <Divider />
            <Stack direction="row" spacing={2} sx={{ px: 2, py: 1 }} alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <StarIcon fontSize="small" sx={{ color: '#f5a623' }} />
                <Typography variant="body2">
                  {entry.ratingAvg > 0 ? entry.ratingAvg.toFixed(1) : '—'}
                  {entry.ratingCount > 0 && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}({entry.ratingCount})
                    </Typography>
                  )}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <FavoriteIcon fontSize="small" color="error" />
                <Typography variant="body2">{entry.favoriteCount}</Typography>
              </Stack>
              {entry.ownerName && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }} noWrap>
                  {entry.ownerName}
                </Typography>
              )}
              {ownedCodes.has(entry.code) && (
                <Tooltip title="Remove from library">
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ ml: entry.ownerName ? 0.5 : 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRemoveError(null);
                      setPendingRemove(entry);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Card>
        ))}
      </Box>

      {selected && (
        <LibraryItemDialog entry={selected} onClose={closeEntry} onDeleted={handleDeleted} />
      )}

      <Dialog
        open={!!pendingRemove}
        onClose={removing ? undefined : () => setPendingRemove(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove from library?</DialogTitle>
        <DialogContent>
          {removeError && <Alert severity="error" sx={{ mb: 2 }}>{removeError}</Alert>}
          <Typography variant="body2" color="text.secondary">
            “{pendingRemove?.title}” will be removed from the public library. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemove(null)} disabled={removing}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove}
            disabled={removing}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function renderContentText(blob: LibraryBlob): string {
  if (blob.type === 'prompt') {
    const c = blob.content as LibraryPromptContent;
    return c.promptText;
  }
  if (blob.type === 'pack') {
    return (blob.content as LibraryPackContent).prompts
      .map((p) => `# ${p.name}\n${p.promptText}`)
      .join('\n\n');
  }
  return (blob.content as LibraryProjectContent).chapters
    .map(
      (ch) =>
        `## ${ch.chapterTitle}\n` + ch.prompts.map((p) => `# ${p.name}\n${p.promptText}`).join('\n\n')
    )
    .join('\n\n');
}

function paiqList(heading: string, items: string[] | undefined) {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>
        {heading}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {items.map((it, i) => (
          <Typography key={i} component="li" variant="body2" color="text.secondary">
            {it}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function PAIQAnalysisView({ analysis }: { analysis: LibraryPaiqAnalysis }) {
  const dims = analysis.dimensions || {};
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          PAIQ Analysis
        </Typography>
        <Chip
          size="small"
          color="success"
          label={`${analysis.paiqGrade} · ${analysis.paiqScore}/${analysis.maxScore || 35}`}
        />
      </Stack>
      {PAIQ_DIMENSION_LABELS.map(([key, label]) => {
        const d = dims[key];
        if (!d) return null;
        return (
          <Box key={key} sx={{ mb: 1.25 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight={600}>
                {label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {d.score}/5
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, (d.score / 5) * 100))}
              sx={{ height: 8, borderRadius: 5, my: 0.5 }}
            />
            {d.reasoning && (
              <Typography variant="caption" color="text.secondary">
                {d.reasoning}
              </Typography>
            )}
          </Box>
        );
      })}
      {paiqList('Strengths', analysis.strengths)}
      {paiqList('Weaknesses', analysis.weaknesses)}
      {paiqList('Suggested Improvements', analysis.improvements)}
    </Box>
  );
}

function LibraryItemDialog({
  entry,
  onClose,
  onDeleted,
}: {
  entry: LibraryBrowseEntry;
  onClose: () => void;
  onDeleted: (code: string) => void;
}) {
  const [detail, setDetail] = useState<LibraryItemDetail | null>(null);
  const [blob, setBlob] = useState<LibraryBlob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    Promise.all([
      libraryApi.getDetail(entry.code).then((r) => r.data),
      libraryApi.getContent(entry.contentHash),
    ])
      .then(([d, b]) => {
        if (!active) return;
        setDetail(d);
        setBlob(b);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load item');
      });
    // Count a view (fire-and-forget, no auth required).
    void libraryApi.trackView(entry.code);
    return () => {
      active = false;
    };
  }, [entry.code, entry.contentHash]);

  const handleShare = async () => {
    const shareUrl = detail?.landingUrl || `${window.location.origin}/library/${entry.code}`;
    const shareData = {
      title: entry.title,
      text: `${entry.title} — a ${entry.type} on PromptFab`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      // user cancelled or share unsupported — fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch {
      setError('Could not share this item.');
    }
  };

  const handleRate = async (stars: number | null) => {
    if (!stars || !detail) return;
    setBusy(true);
    try {
      const res = await libraryApi.rate(entry.code, stars);
      setDetail({
        ...detail,
        yourRating: res.data.yourRating,
        ratingAvg: res.data.ratingAvg,
        ratingCount: res.data.ratingCount,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit rating');
    } finally {
      setBusy(false);
    }
  };

  const handleFavorite = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await libraryApi.favorite(entry.code);
      setDetail({ ...detail, favorited: res.data.favorited, favoriteCount: res.data.favoriteCount });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update favorite');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!blob) return;
    await navigator.clipboard.writeText(renderContentText(blob));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleLoadIntoRunner = () => {
    if (!blob) return;
    navigate(`/runner?template=${encodeURIComponent(renderContentText(blob))}`);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await libraryApi.unpublish(entry.code);
      onDeleted(entry.code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove item');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Chip size="small" color={TYPE_COLOR[entry.type]} label={TYPE_LABEL[entry.type]} />
          {typeof entry.paiqScore === 'number' && (
            <Chip size="small" variant="outlined" label={`PAIQ ${entry.paiqScore}/35`} />
          )}
        </Stack>
        {entry.title}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {entry.description || 'No description provided.'}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Your rating:</Typography>
            <Rating
              value={detail?.yourRating ?? 0}
              disabled={busy || !detail}
              onChange={(_, v) => handleRate(v)}
              emptyIcon={<StarBorderIcon fontSize="inherit" />}
            />
          </Stack>
          <Tooltip title={detail?.favorited ? 'Remove favorite' : 'Add to favorites'}>
            <span>
              <IconButton onClick={handleFavorite} disabled={busy || !detail} color="error">
                {detail?.favorited ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            {detail
              ? `${detail.ratingAvg > 0 ? detail.ratingAvg.toFixed(1) : '—'} avg · ${
                  detail.ratingCount
                } ratings · ${detail.favoriteCount} favorites`
              : ''}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            disabled={!blob}
          >
            {copied ? 'Copied!' : 'Copy content'}
          </Button>
          {entry.type === 'prompt' && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              onClick={handleLoadIntoRunner}
              disabled={!blob}
            >
              Load into Runner
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShare}
          >
            {shared ? 'Link copied!' : 'Share'}
          </Button>
          {detail?.landingUrl && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              component="a"
              href={detail.landingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Landing page
            </Button>
          )}
        </Stack>

        {!blob && !error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          blob && (
            <Box
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 2,
                fontSize: '0.85rem',
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {renderContentText(blob)}
            </Box>
          )
        )}

        {detail?.paiqAnalysis && <PAIQAnalysisView analysis={detail.paiqAnalysis} />}
      </DialogContent>
      {detail?.isOwner && (
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
          {confirmDelete ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
                Remove this {entry.type} from the public library? This cannot be undone.
              </Typography>
              <Button size="small" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Removing…' : 'Confirm remove'}
              </Button>
            </Stack>
          ) : (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirmDelete(true)}
            >
              Remove from library
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
